export function toTitleCase(str?: string, allTitles: boolean = false) {
  if (!str) return '';
  if (!allTitles) return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  if (allTitles) return str.replace(/\w+/g, (word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase());
  return '';
}

// returns 1 000 000
export function shortNumberFormat(number: number | undefined) {
  if (!number) return '';
  return number.toLocaleString('en-US', { notation: 'compact' });
}

// returns 1M
export function spacedNumberFormat(number: number | undefined) {
  return number?.toLocaleString('fr-FR');
}

export function formatTimeInSec_DDHHMM(seconds: number, signed?: boolean) {
  if (!seconds) {
    return '-';
  }

  let days = Math.floor(seconds / (3600 * 24));
  let hours = Math.floor((seconds - days * 3600 * 24) / 3600);
  let minutes = Math.floor((seconds - days * 3600 * 24 - hours * 3600) / 60);

  let result = !signed ? '' : seconds > 0 ? '+' : '-';
  if (days > 0) {
    result += `${days}D`;
    hours > 0 && (result += ` ${hours}H`);
  } else {
    result += `${hours}H`;
    minutes > 0 && (result += ` ${minutes}M`);
  }

  return result;
}

export function formatDurationFromMs(milliseconds: number): string {
  if (!milliseconds || milliseconds <= 0) {
    return '0 sec';
  }

  const totalSeconds = Math.round(milliseconds / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (totalMinutes < 1) {
    return `${seconds} sec`;
  }

  if (totalMinutes < 60) {
    return seconds === 0 ? `${totalMinutes} min` : `${totalMinutes} min ${seconds} sec`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0 && seconds === 0) {
    return `${hours} h`;
  }

  if (seconds === 0) {
    return `${hours} h ${minutes} min`;
  }

  if (minutes === 0) {
    return `${hours} h ${seconds} sec`;
  }

  return `${hours} h ${minutes} min ${seconds} sec`;
}

export function signedPercentage(value: number) {
  if (!value || value === 0) return '';
  if (value === Infinity) return '+100%';
  if (value > 0) return `+${value}%`;
  return `${value}%`;
}

export interface PercentageFormatConfig {
  decimals?: number;
  showSign?: boolean;
  fallback?: string;
}

/**
 * Formats a decimal value (0-1) as a percentage string
 * @param value - The decimal value to format (0-1 range)
 * @param config - Configuration options for formatting
 * @returns Formatted percentage string (e.g., "85.5%")
 */
export function formatPercentage(percentage: number | null | undefined, config: PercentageFormatConfig = {}): string {
  const { decimals = 0, showSign = false, fallback = 'N/A' } = config;

  if (percentage === null || percentage === undefined || isNaN(percentage)) {
    return fallback;
  }

  const formatted = percentage.toFixed(decimals);
  const sign = showSign && percentage > 0 ? '+' : '';

  return `${sign}${formatted}%`;
}

export function flattenJson(obj: any, parentPath = ''): Record<string, string | string[]> {
  const result: Record<string, any> = {};

  for (const key in obj) {
    const path = parentPath ? `${parentPath}.${key}` : key;
    const value = obj[key];

    if (Array.isArray(value)) {
      // Store full array at path
      result[path] = value;

      // Store individual elements with index
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          Object.assign(result, flattenJson(item, `${path}.${index}`));
        } else {
          result[`${path}.${index}`] = item;
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      Object.assign(result, flattenJson(value, path));
    } else {
      result[path] = value;
    }
  }

  return result;
}

export function cleanSpecialCharacters(str: string) {
  return str.replace(/[^a-zA-Z0-9]/g, '_');
}

/**
 * Normalizes a Salesforce tag ID by cleaning special characters and handling edge cases
 * @param selectedField - The Salesforce field name
 * @param optionValue - The option value to normalize
 * @returns A normalized tag ID in the format sf_{field}_{normalizedOption}
 */
export function normalizeSalesforceTagId(selectedField: string, optionValue: string): string {
  const normalizedOption = cleanSpecialCharacters(optionValue.toLowerCase())
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

  return `sf_${selectedField}_${normalizedOption}`;
}
